import { BALANCE } from '../config/balance';
import type { RngStream } from '../core/rng';
import type {
  BuildState,
  EvolutionId,
  OfferId,
  PassiveId,
  PlayerStats,
  WeaponId,
} from '../core/types';

export const WEAPON_IDS: readonly WeaponId[] = ['boomerang', 'staff', 'whip', 'arrow'];
export const PASSIVE_IDS: readonly PassiveId[] = [
  'attack_speed', 'area', 'projectile_speed', 'magnet',
  'crit', 'shield', 'calm', 'greed',
];
export const EVOLUTION_IDS: readonly EvolutionId[] = [
  'multi_boomerang', 'executioner', 'arcane_storm', 'sun_piercer',
];

/** 开局构筑：一把指定武器（默认回旋镖），其余武器 / 被动全空。 */
export function initialBuild(starting: WeaponId = 'boomerang'): BuildState {
  return {
    weapon: {
      boomerang: starting === 'boomerang' ? 1 : 0,
      staff: starting === 'staff' ? 1 : 0,
      whip: starting === 'whip' ? 1 : 0,
      arrow: starting === 'arrow' ? 1 : 0,
    },
    passive: {
      attack_speed: 0, area: 0, projectile_speed: 0, magnet: 0,
      crit: 0, shield: 0, calm: 0, greed: 0,
    },
    evolved: {
      multi_boomerang: false, executioner: false,
      arcane_storm: false, sun_piercer: false,
    },
  };
}

/** 由构筑等级重算面板（升级 / 进化后调用）。 */
export function recomputeStats(b: BuildState): PlayerStats {
  const p = BALANCE.passives;
  const pl = BALANCE.player;
  return {
    maxHp: pl.maxHp,
    speed: pl.speed,
    pickupRadius: pl.pickupRadius * (1 + p.magnet.per * b.passive.magnet),
    areaMul: 1 + p.area.per * b.passive.area,
    attackSpeedMul: 1 + p.attack_speed.per * b.passive.attack_speed,
    projectileSpeedMul: 1 + p.projectile_speed.per * b.passive.projectile_speed,
    critChance: p.crit.chancePer * b.passive.crit,
    critMul: p.crit.mul,
    calmPer: p.calm.per * b.passive.calm,
    goldMul: 1 + p.greed.per * b.passive.greed,
    shield: b.passive.shield > 0,
  };
}

function evolutionOfWeapon(b: BuildState, w: WeaponId): boolean {
  for (const id of EVOLUTION_IDS) {
    if (BALANCE.evolutions[id].weapon === w && b.evolved[id]) return true;
  }
  return false;
}

/** 可进三选一的条目（未满级武器 / 被动；已进化武器不再升级）。 */
export function eligibleOffers(b: BuildState): OfferId[] {
  const out: OfferId[] = [];
  for (const w of WEAPON_IDS) {
    if (evolutionOfWeapon(b, w)) continue;
    if (b.weapon[w] < BALANCE.weapons[w].max) out.push(w);
  }
  for (const p of PASSIVE_IDS) {
    if (b.passive[p] < BALANCE.passives[p].max) out.push(p);
  }
  return out;
}

function offerWeight(b: BuildState, o: OfferId): number {
  if (o === 'boomerang' || o === 'staff' || o === 'whip' || o === 'arrow') {
    const base = BALANCE.weapons[o].weight;
    return b.weapon[o] === 0 ? base * 1.4 : base;
  }
  return BALANCE.passives[o].weight;
}

/** 三选一：offers 流无放回抽 3；exclude 用于 reroll 避开当前选项。 */
export function drawOffers(
  rng: RngStream,
  b: BuildState,
  exclude: readonly OfferId[] = [],
): OfferId[] {
  const pool = eligibleOffers(b).filter((o) => !exclude.includes(o));
  const weights = pool.map((o) => offerWeight(b, o));
  const chosen: OfferId[] = [];
  while (chosen.length < 3 && pool.length > 0) {
    const picked = rng.weighted(pool, weights);
    chosen.push(picked);
    const i = pool.indexOf(picked);
    pool.splice(i, 1);
    weights.splice(i, 1);
  }
  return chosen;
}

/** 已满足「武器 + 指定被动双满级」但尚未进化的进化。 */
export function readyEvolutions(b: BuildState): EvolutionId[] {
  const out: EvolutionId[] = [];
  for (const id of EVOLUTION_IDS) {
    const e = BALANCE.evolutions[id];
    if (b.evolved[id]) continue;
    const wMax = b.weapon[e.weapon] >= BALANCE.weapons[e.weapon].max;
    const pMax = b.passive[e.passive] >= BALANCE.passives[e.passive].max;
    if (wMax && pMax) out.push(id);
  }
  return out;
}

/** 伤害结算：冷静乘区（血越少越高）+ 暴击。暴击随机走 fx 流，不碰 world 流。 */
export function rollDamage(
  base: number,
  hpRatio: number,
  stats: PlayerStats,
  rng: RngStream,
): { amount: number; crit: boolean } {
  const calm = 1 + stats.calmPer * (1 - hpRatio);
  let amount = base * calm;
  const crit = rng.chance(stats.critChance);
  if (crit) amount *= stats.critMul;
  return { amount, crit };
}
